import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { SectionTeacherProjectTeachingTime, SectionTeacherRow, SubjectCreateOption, SubjectTypeOption, TeacherOption, TeacherWorkloadMode, TeachingRangeOption, TeachingTypeOption } from 'src/app/services/models/subjects/subject.model';
import { SubjectScheduleGroupCreatePayload, SubjectScheduleGroupCreateResponse } from 'src/app/services/models/subject-schedules/subject-schedule.model';
import { ConfirmDialogComponent } from 'src/shared/confirm-dialog/confirm-dialog.component';
import { SharedModule } from 'src/shared/shared.module';
import { AuthService } from '../../auth/services/auth.service';
import { ProjectTeacherModalComponent } from './components/project-teacher-modal/project-teacher-modal.component';
import { SectionEditorComponent } from './components/section-editor/section-editor.component';
import { SubjectInfoFormComponent } from './components/subject-info-form/subject-info-form.component';
import { TeacherManagementModalComponent } from './components/teacher-management-modal/teacher-management-modal.component';
import { SubjectManagementCreateFacade } from './application/subject-management-create.facade';
import {
  SubjectApprovalBuilderInput,
  areApprovalTeachersReady,
  buildSubjectApprovalPayload,
} from './domain/subject-management-create-approval.builder';
import {
  buildApprovalAdapterInput,
  getApprovalStatusLabel,
} from './domain/subject-management-create-approval.adapter';
import {
  createProjectTeachingTimeForm,
  createSectionForm,
  createTeacherDraftForm,
  createTeachingTimeForm,
  reindexSectionState,
  updateSectionDuplicateErrors,
} from './forms/subject-management-create.forms';
import {
  deriveTeachingWorkloadHoursFromCourseunit,
  getCurrentBuddhistYear,
  toBuddhistYear,
  toChristianYear,
  toNullableString,
  toNumberOrNull,
} from './domain/subject-management-create.utils';
import {
  TeachingTimeValue,
  buildProjectTeacherRow,
  buildRegularTeacherRow,
  buildTeachingRangeOptions,
  calculateProjectWorkloadHours,
  normalizeTeachingRangeValuesToOptions,
} from './domain/subject-management-create-teacher.builder';
import { SubjectManagementCreateOptionsStore } from './application/subject-management-create-options.store';
import {
  SubjectManagementCreateDeleteContext,
  SubjectManagementCreatePendingDelete,
  getPendingDeleteMessage,
  isPendingDeleteValid,
} from './domain/subject-management-create-delete';

type CreateSectionStatus =
  | 'pending'
  | 'pending_approval'
  | 'approved'
  | 'special_sent_back'
  | 'sent_back'
  | 'in_progress'
  | 'draft';

// ENOT hotfix (release 1): the certification/approval flow is not part of this
// release, so จัดการรายวิชา only ever surfaces "แบบร่าง" and "อยู่ระหว่างดำเนินการ".
// Any non-draft status collapses to the "in progress" placeholder below.
const PHASE_ONE_SECTION_STATUS: CreateSectionStatus = 'in_progress';
const PHASE_ONE_SECTION_STATUS_LABEL = 'อยู่ระหว่างดำเนินการ';
const COURSEUNIT_PATTERN = /^\d+\s?\(\d+-\d+-\d+\)$/;

@Component({
  selector: 'app-subject-management-create',
  imports: [
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
    SubjectInfoFormComponent,
  ],
  templateUrl: './subject-management-create.component.html',
  styleUrl: './subject-management-create.component.scss',
  providers: [SubjectManagementCreateOptionsStore],
})
export class SubjectManagementCreateComponent implements OnInit {
  @ViewChild('deleteSectionConfirmDialog')
  private deleteSectionConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('saveSubjectConfirmDialog')
  private saveSubjectConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('deleteSubjectConfirmDialog')
  private deleteSubjectConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('teacherSectionLockConfirmDialog')
  private teacherSectionLockConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('submitApprovalConfirmDialog')
  private submitApprovalConfirmDialog?: ConfirmDialogComponent;

  @ViewChild('submitApprovalErrorDialog')
  private submitApprovalErrorDialog?: ConfirmDialogComponent;

  @ViewChild('saveSubjectErrorDialog')
  private saveSubjectErrorDialog?: ConfirmDialogComponent;

  @ViewChild('submitApprovalSuccessDialog')
  private submitApprovalSuccessDialog?: ConfirmDialogComponent;

  @ViewChild('deleteItemWarningDialog')
  private deleteItemWarningDialog?: ConfirmDialogComponent;

  @ViewChild(TeacherManagementModalComponent)
  private teacherManagementModal?: TeacherManagementModalComponent;

  @ViewChild(ProjectTeacherModalComponent)
  private projectTeacherModal?: ProjectTeacherModalComponent;

  private fb = inject(FormBuilder);
  private auth = inject(AuthService);

  readonly projectResearchSubjectTypeCode = 'SPC';
  readonly academicTermOptions = ['1', '2', '3'];
  readonly yearOptions = this.buildYearOptions();
  readonly teachingDayOptions = ['วันจันทร์', 'วันอังคาร', 'วันพุธ', 'วันพฤหัสบดี', 'วันศุกร์', 'วันเสาร์', 'วันอาทิตย์'];
  readonly timeOptions = [
    '07:00',
    '07:30',
    '08:00',
    '08:30',
    '09:00',
    '09:30',
    '10:00',
    '10:30',
    '11:00',
    '11:30',
    '12:00',
    '12:30',
    '13:00',
    '13:30',
    '14:00',
    '14:30',
    '15:00',
    '15:30',
    '16:00',
    '16:30',
    '17:00',
    '17:30',
    '18:00',
    '18:30',
    '19:00',
    '19:30',
    '20:00',
    '20:30',
    '21:00',
  ];
  submitted = false;
  projectTeacherSubmitted = false;
  isSaved = false;
  sectionDeleteIndex: number | null = null;
  activeTeacherSectionIndex: number | null = null;
  editingProjectTeacherIndex: number | null = null;
  pendingTeacherSectionIndex: number | null = null;
  pendingApprovalSectionIndex: number | null = null;
  pendingDelete: SubjectManagementCreatePendingDelete | null = null;
  isSubmittingApproval = false;
  submitApprovalErrorMessage = '';
  saveSubjectErrorMessage = '';
  teacherTeachingRangeOptions: TeachingRangeOption[] = [];
  teacherRowsBySection: Record<number, SectionTeacherRow[]> = {};
  teacherWorkloadModeBySection: Record<number, TeacherWorkloadMode> = {};
  teacherSectionLockedBySection: Partial<Record<number, boolean>> = {};
  isSavingTeachers = false;
  teacherSaveError = '';
  submittedSectionStatusBySection: Partial<Record<number, string>> = {};
  submittedSectionStatusCodeBySection: Partial<Record<number, CreateSectionStatus>> = {};
  sectionExpandedBySection: Partial<Record<number, boolean>> = {};
  subjectApprovalStatusLabel: string | null = null;
  subjectApprovalStatus: CreateSectionStatus | null = null;

  private isHydratingTeacherModal = false;
  private router = inject(Router);
  private facade = inject(SubjectManagementCreateFacade);
  private optionsStore = inject(SubjectManagementCreateOptionsStore);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private readonly digitsOnlyValidators = [Validators.required, Validators.pattern(/^\d+$/)];
  subjectForm = this.fb.group({
    academicTerm: [{ value: this.getSessionAcademicTermName(), disabled: true }, Validators.required],
    year: [{ value: String(this.getDefaultBuddhistYear()), disabled: true }, Validators.required],
    subjectType: [null as number | null, Validators.required],
    subjectCode: [{ value: null as number | null, disabled: true }, Validators.required],
    subjectName: [{ value: '', disabled: true }, Validators.required],
    // Display-only curriculum name (used in the saved preview). The editable
    // value lives in curriculumTypeId below.
    curriculum: [{ value: '', disabled: true }],
    curriculumTypeId: [null as number | null, Validators.required],
    creditDescription: ['', [Validators.required, Validators.pattern(COURSEUNIT_PATTERN)]],
  });

  sectionsForm = this.fb.group({
    sections: this.fb.array<FormGroup>([]),
  });

  teacherModalForm = this.fb.group({
    workloadMode: ['shared'],
    teachers: this.fb.array<FormGroup>([]),
  });

  projectTeacherForm = this.fb.group({
    userId: [null as number | null, Validators.required],
    department: [{ value: '', disabled: true }],
    // Teaching slots are optional for project/research advisors.
    teachingTimes: this.fb.array<FormGroup>([this.createProjectTeachingTimeForm()]),
    workloadHours: [{ value: '0', disabled: true }],
    primaryStudentCount: ['', this.digitsOnlyValidators],
    coStudentCount: ['', this.digitsOnlyValidators],
    description: [''],
    showInSchedule: [false],
  });

  ngOnInit(): void {
    effect(() => {
      this.clearSelectedSubjectDetailForSubjectOptions(this.subjectOptions);
    }, { injector: this.injector });
    effect(() => {
      const _selectedAcademicTermId = this.selectedAcademicTermId;
      this.optionsStore.loadSubjectOptions(this.getSelectedSubjectTypeId());
    }, { injector: this.injector });

    this.subjectForm.get('subjectCode')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((subjectId) => {
        this.applySubject(subjectId);
      });
    this.subjectForm.get('academicTerm')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resolveAcademicTermOptions());
    this.subjectForm.get('year')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.resolveAcademicTermOptions());
    this.subjectForm.get('subjectType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.syncSubjectCodeEnabledState();
        this.clearSelectedSubjectDetail();
        this.optionsStore.loadSubjectOptions(this.getSelectedSubjectTypeId());
      });
    this.teacherModalForm.controls.workloadMode.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onTeacherWorkloadModeChanged());
    this.optionsStore.loadReferenceOptions();
    this.resolveAcademicTermOptions();
    this.syncSubjectCodeEnabledState();
  }

  get selectedAcademicTermId(): number | null {
    return this.optionsStore.selectedAcademicTermId();
  }

  get selectedAcademicTerm() {
    return this.optionsStore.selectedAcademicTerm();
  }

  get subjectTypeOptions(): SubjectTypeOption[] {
    return this.optionsStore.subjectTypeOptions();
  }

  get subjectOptions(): SubjectCreateOption[] {
    return this.optionsStore.subjectOptions();
  }

  get curriculumTypeOptions() {
    return this.optionsStore.curriculumTypeOptions();
  }

  get isSelectedSubjectSynced(): boolean {
    return this.getSelectedSubject()?.isSynced === true;
  }

  get teachingTypeOptions(): TeachingTypeOption[] {
    return this.optionsStore.teachingTypeOptions();
  }

  get teacherOptions(): TeacherOption[] {
    return this.optionsStore.teacherOptions();
  }

  get pendingDeleteMessage(): string {
    return getPendingDeleteMessage(this.pendingDelete);
  }

  get sections(): FormArray<FormGroup> {
    return this.sectionsForm.get('sections') as FormArray<FormGroup>;
  }

  get sectionForms(): FormGroup[] {
    return this.sections.controls ;
  }

  get teacherDraftRows(): FormArray<FormGroup> {
    return this.teacherModalForm.get('teachers') as FormArray<FormGroup>;
  }

  get projectTeachingTimes(): FormArray<FormGroup> {
    return this.projectTeacherForm.get('teachingTimes') as FormArray<FormGroup>;
  }

  onSaveSubject(): void {
    const selectedSubject = this.getSelectedSubject();

    const academicTermId = this.selectedAcademicTermId;
    const rawValue = this.subjectForm.getRawValue();
    const subjectId = Number(rawValue.subjectCode);
    const curriculumTypeId = Number(rawValue.curriculumTypeId);
    const studyModeId = this.getSelectedSubjectTypeId();
    const courseunit = String(rawValue.creditDescription ?? '').trim();

    if (
      !selectedSubject
      || this.subjectForm.invalid
      || academicTermId == null
      || !Number.isFinite(subjectId)
      || !Number.isFinite(curriculumTypeId)
      || studyModeId == null
    ) {
      this.subjectForm.markAllAsTouched();
      return;
    }

    this.applyLocalSubjectEdits(selectedSubject);
    this.isSubmittingApproval = true;
    this.facade.createSubjectOffering({
      subject_offering: {
        academic_term_id: academicTermId,
        subject_id: subjectId,
        study_mode_id: studyModeId,
        curriculum_type_id: curriculumTypeId,
        courseunit,
      },
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ({ subject_offering: offering }) => {
        this.isSubmittingApproval = false;
        this.isSaved = true;
        this.subjectForm.disable({ emitEvent: false });
        this.saveSubjectConfirmDialog?.close();
        void this.router.navigate(['/subject-management/details'], {
          queryParams: {
            offering_id: offering.id,
            type: offering.type,
            types: offering.types.join(','),
          },
        });
      },
      error: (error) => {
        this.isSubmittingApproval = false;
        this.saveSubjectConfirmDialog?.close();
        this.openSaveSubjectErrorDialog(
          this.extractApiErrorMessage(error, 'ไม่สามารถบันทึกรายวิชาได้ กรุณาลองใหม่อีกครั้ง'),
        );
      },
    });
  }

  onAddSection(): void {
    this.addSection();
  }

  onRequestRemoveSection(sectionIndex: number): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    if (this.isSectionSubmitted(sectionIndex)) {
      return;
    }

    this.sectionDeleteIndex = sectionIndex;
    this.deleteSectionConfirmDialog?.open();
  }

  onRequestSaveSubject(): void {
    this.submitted = true;

    if (this.subjectForm.invalid) {
      this.subjectForm.markAllAsTouched();
      return;
    }

    this.saveSubjectConfirmDialog?.open();
  }

  onConfirmRemoveSection(): void {
    if (this.sectionDeleteIndex == null) {
      return;
    }

    this.removeSection(this.sectionDeleteIndex);
    this.sectionDeleteIndex = null;
    this.deleteSectionConfirmDialog?.close();
  }

  onCancelRemoveSection(): void {
    this.sectionDeleteIndex = null;
  }

  onRequestDeleteSubject(): void {
    this.deleteSubjectConfirmDialog?.open();
  }

  onConfirmDeleteSubject(): void {
    // "ลบรายวิชา" discards the in-progress offering configuration (available only
    // before any section is submitted). It must NOT touch the master subject — just
    // navigate back to the list.
    this.deleteSubjectConfirmDialog?.close();
    void this.router.navigate(['/subject-management']);
  }

  private removeSection(sectionIndex: number): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    this.sections.removeAt(sectionIndex);
    this.reindexTeacherRowsAfterSectionRemoval(sectionIndex);
    this.updateSectionDuplicateErrors();
  }

  onAddTeachingTime(sectionIndex: number): void {
    if (this.isTeacherSectionLocked(sectionIndex)) {
      return;
    }

    const section = this.sections.at(sectionIndex);
    this.getTeachingTimes(section).push(this.createTeachingTimeForm());
  }

  onRemoveTeachingTime(sectionIndex: number, timeIndex: number): void {
    this.requestPendingDelete({ type: 'sectionTeachingTime', sectionIndex, itemIndex: timeIndex });
  }

  onManageTeachers(sectionIndex: number): void {
    if (this.isSectionSubmitted(sectionIndex) && !this.isSectionSpecialSentBack(sectionIndex)) {
      return;
    }

    if (this.isTeacherSectionLocked(sectionIndex)) {
      this.openTeacherManagementForSection(sectionIndex);
      return;
    }

    if (!this.isSectionReadyForTeacherModal(sectionIndex)) {
      this.markSectionForTeacherModalValidation(sectionIndex);
      return;
    }

    if (this.isProjectResearchSubjectType()) {
      this.openTeacherManagementForSection(sectionIndex);
      return;
    }

    this.pendingTeacherSectionIndex = sectionIndex;
    this.teacherSectionLockConfirmDialog?.open();
  }

  onConfirmTeacherSectionLock(): void {
    if (this.pendingTeacherSectionIndex == null) {
      this.teacherSectionLockConfirmDialog?.close();
      return;
    }

    const sectionIndex = this.pendingTeacherSectionIndex;

    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      this.pendingTeacherSectionIndex = null;
      this.teacherSectionLockConfirmDialog?.close();
      return;
    }

    this.lockTeacherSection(sectionIndex);
    this.pendingTeacherSectionIndex = null;
    this.teacherSectionLockConfirmDialog?.close();
    this.openTeacherManagementForSection(sectionIndex);
  }

  onCancelTeacherSectionLock(): void {
    this.pendingTeacherSectionIndex = null;
  }

  isTeacherSectionLocked(sectionIndex: number): boolean {
    return this.teacherSectionLockedBySection[sectionIndex] ?? false;
  }

  private openTeacherManagementForSection(sectionIndex: number): void {
    if (this.isProjectResearchSubjectType()) {
      this.openProjectTeacherModal(sectionIndex);
      return;
    }

    const savedRows = [...this.getSectionTeachers(sectionIndex)];
    const savedWorkloadMode = this.teacherWorkloadModeBySection[sectionIndex] ?? 'shared';

    this.isHydratingTeacherModal = true;
    this.activeTeacherSectionIndex = sectionIndex;
    this.teacherModalForm.controls.workloadMode.setValue(
      savedWorkloadMode,
      { emitEvent: false }
    );
    const fallbackOptions = buildTeachingRangeOptions({
      teachingDays: [],
      fallbackRanges: savedRows.map((row) => row.teachingRange),
    });
    this.teacherTeachingRangeOptions = fallbackOptions;
    this.resetTeacherDraftRows(savedRows);
    this.openTeacherModal();
    this.loadTeacherTeachingRangeOptions(sectionIndex)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => {
        if (this.activeTeacherSectionIndex === sectionIndex) {
          // `options` already folds in this section's saved teaching ranges as its
          // own exam-aware fallback (see loadTeacherTeachingRangeOptions), so
          // re-merging the unfiltered synchronous `fallbackOptions` placeholder here
          // would resurrect exam-window dates that were correctly excluded above.
          this.teacherTeachingRangeOptions = options;
          this.normalizeTeacherDraftTeachingRanges();
        }
        this.isHydratingTeacherModal = false;
      });
  }

  onAddTeacherDraftRow(): void {
    if (this.activeTeacherSectionIndex == null) {
      return;
    }

    this.teacherDraftRows.push(this.createTeacherDraftForm());
  }

  private normalizeTeacherDraftTeachingRanges(): void {
    this.teacherDraftRows.controls.forEach((row) => {
      row.get('teachingRange')?.setValue(
        normalizeTeachingRangeValuesToOptions(
          row.get('teachingRange')?.value,
          this.teacherTeachingRangeOptions
        ),
        { emitEvent: false }
      );
    });
  }

  onRemoveTeacherDraftRow(rowIndex: number): void {
    this.requestPendingDelete({ type: 'teacherDraft', itemIndex: rowIndex });
  }

  onTeacherSelected(rowIndex: number, teacher?: TeacherOption): void {
if (rowIndex < 0 || rowIndex >= this.teacherDraftRows.length) {
  return;
}

const row = this.teacherDraftRows.at(rowIndex);

    row.patchValue({
      fullName: teacher?.fullName ?? '',
      department: teacher?.department ?? '-',
    }, { emitEvent: false });
  }

  onSaveTeachers(): void {
    if (this.activeTeacherSectionIndex == null) {
      return;
    }

    const sectionIndex = this.activeTeacherSectionIndex;
    const workloadMode = this.getSelectedTeacherWorkloadMode();
    this.teacherWorkloadModeBySection[sectionIndex] = workloadMode;
    this.syncSectionWorkloadHours(sectionIndex);
    this.teacherRowsBySection[sectionIndex] = this.teacherDraftRows.controls
      .map((control) => buildRegularTeacherRow({
        draft: control.getRawValue(),
        teacherOptions: this.teacherOptions,
        workloadHours: this.getWorkloadHoursText(sectionIndex),
        workloadMode,
        teachingTimes: this.getTeachingTimeValues(sectionIndex),
      }))
      .filter((row): row is SectionTeacherRow => row != null);
    this.closeTeacherModal();
  }

  onCancelTeachers(): void {
    if (this.activeTeacherSectionIndex != null) {
      this.syncSectionWorkloadHours(this.activeTeacherSectionIndex);
    }

    this.closeTeacherModal();
  }

  private onTeacherWorkloadModeChanged(): void {
    if (this.isHydratingTeacherModal || this.activeTeacherSectionIndex == null) {
      return;
    }

    this.syncSectionWorkloadHours(
      this.activeTeacherSectionIndex,
      this.getSelectedTeacherWorkloadMode()
    );
    this.teacherDraftRows.clear();
  }

  onProjectTeacherSelected(teacher?: TeacherOption): void {
    this.projectTeacherForm.patchValue({
      department: teacher?.department ?? '-',
    }, { emitEvent: false });
  }

  onAddProjectTeachingTime(): void {
    this.projectTeachingTimes.push(this.createProjectTeachingTimeForm());
    this.updateProjectWorkloadHours();
  }

  onRemoveProjectTeachingTime(timeIndex: number): void {
    this.requestPendingDelete({ type: 'projectTeachingTime', itemIndex: timeIndex });
  }

  onEditProjectTeacher(sectionIndex: number, teacherIndex: number): void {
    if (
      !this.isProjectResearchSubjectType()
      || this.isSectionSubmitted(sectionIndex)
      || sectionIndex < 0
      || sectionIndex >= this.sections.length
    ) {
      return;
    }

    const sectionTeachers = this.getSectionTeachers(sectionIndex);

    if (teacherIndex < 0 || teacherIndex >= sectionTeachers.length) {
      return;
    }

    const row = sectionTeachers[teacherIndex];
    this.activeTeacherSectionIndex = sectionIndex;
    this.editingProjectTeacherIndex = teacherIndex;
    this.resetProjectTeacherForm();
    this.projectTeachingTimes.clear();

    const savedTeachingTimes = row.projectTeachingTimes ?? [];
    const teachingTimes = savedTeachingTimes.length > 0
      ? savedTeachingTimes
      : [{ teachingDay: null, startTime: null, endTime: null }];

    teachingTimes.forEach((time) => {
      this.projectTeachingTimes.push(this.createProjectTeachingTimeForm(time));
    });
    this.projectTeacherForm.patchValue({
      userId: row.userId,
      department: row.department,
      workloadHours: row.workloadHours,
      primaryStudentCount: row.primaryAdvisorStudentCount == null
        ? ''
        : String(row.primaryAdvisorStudentCount),
      coStudentCount: row.coAdvisorStudentCount == null
        ? ''
        : String(row.coAdvisorStudentCount),
      description: row.description ?? '',
      showInSchedule: row.showInTimetable === true,
    }, { emitEvent: false });
    this.projectTeachingTimes.updateValueAndValidity({ emitEvent: false });
    this.updateProjectWorkloadHours();

    this.projectTeacherModal?.open();
  }

  onRemoveProjectTeacher(sectionIndex: number, teacherIndex: number): void {
    this.requestPendingDelete({ type: 'projectTeacher', sectionIndex, itemIndex: teacherIndex });
  }

  onConfirmPendingDelete(): void {
    const pendingDelete = this.pendingDelete;

    if (pendingDelete == null || !isPendingDeleteValid(pendingDelete, this.getDeleteContext())) {
      this.clearPendingDelete();
      return;
    }

    switch (pendingDelete.type) {
      case 'sectionTeachingTime':
        this.getTeachingTimes(this.sections.at(pendingDelete.sectionIndex)).removeAt(pendingDelete.itemIndex);
        break;
      case 'teacherDraft':
        this.teacherDraftRows.removeAt(pendingDelete.itemIndex);
        break;
      case 'projectTeachingTime':
        this.projectTeachingTimes.removeAt(pendingDelete.itemIndex);
        this.projectTeachingTimes.markAsDirty();
        this.projectTeachingTimes.updateValueAndValidity();
        this.updateProjectWorkloadHours();
        break;
      case 'projectTeacher': {
        const rows = [...this.getSectionTeachers(pendingDelete.sectionIndex)];
        rows.splice(pendingDelete.itemIndex, 1);
        this.teacherRowsBySection[pendingDelete.sectionIndex] = rows;
        break;
      }
    }

    this.clearPendingDelete();
  }

  onCancelPendingDelete(): void {
    this.pendingDelete = null;
  }

  private requestPendingDelete(pendingDelete: SubjectManagementCreatePendingDelete): void {
    if (!isPendingDeleteValid(pendingDelete, this.getDeleteContext())) {
      return;
    }

    this.pendingDelete = pendingDelete;
    this.deleteItemWarningDialog?.open();
  }

  private clearPendingDelete(): void {
    this.pendingDelete = null;
    this.deleteItemWarningDialog?.close();
  }

  private getDeleteContext(): SubjectManagementCreateDeleteContext {
    const sectionTeachingTimeCounts: Record<number, number> = {};
    const submittedSections: Record<number, boolean> = {};
    const projectTeacherCounts: Record<number, number> = {};

    this.sectionForms.forEach((section, sectionIndex) => {
      sectionTeachingTimeCounts[sectionIndex] = this.getTeachingTimes(section).length;
      submittedSections[sectionIndex] = this.isSectionSubmitted(sectionIndex);
      projectTeacherCounts[sectionIndex] = this.getSectionTeachers(sectionIndex).length;
    });

    return {
      sectionCount: this.sections.length,
      sectionTeachingTimeCounts,
      lockedSections: this.teacherSectionLockedBySection,
      submittedSections,
      teacherDraftCount: this.teacherDraftRows.length,
      projectTeachingTimeCount: this.projectTeachingTimes.length,
      projectTeacherCounts,
      isProjectResearch: this.isProjectResearchSubjectType(),
      activeTeacherSectionIndex: this.activeTeacherSectionIndex,
    };
  }

  onSaveProjectTeacher(): void {
    this.projectTeacherSubmitted = true;
    this.projectTeacherForm.markAllAsTouched();
    this.projectTeachingTimes.updateValueAndValidity();
    this.updateProjectWorkloadHours();

    if (this.activeTeacherSectionIndex == null || this.projectTeacherForm.invalid) {
      return;
    }

    const sectionIndex = this.activeTeacherSectionIndex;
    const rawValue = this.projectTeacherForm.getRawValue();
    const row = buildProjectTeacherRow({
      formValue: rawValue,
      teacherOptions: this.teacherOptions,
      teachingTimes: this.getProjectTeachingTimeValues(),
    });

    if (row == null) {
      return;
    }

    const rows = [...(this.teacherRowsBySection[sectionIndex] ?? [])];

    if (
      this.editingProjectTeacherIndex != null
      && this.editingProjectTeacherIndex >= 0
      && this.editingProjectTeacherIndex < rows.length
    ) {
      rows[this.editingProjectTeacherIndex] = row;
    } else {
      rows.push(row);
    }

    this.teacherRowsBySection[sectionIndex] = rows;
    this.closeProjectTeacherModal();
  }

  onCancelProjectTeacher(): void {
    this.closeProjectTeacherModal();
  }

  onSubmitApproval(sectionIndex: number): void {
    if (!this.isSectionReadyForApproval(sectionIndex)) {
      return;
    }

    this.pendingApprovalSectionIndex = sectionIndex;
    this.submitApprovalConfirmDialog?.open();
  }

  onSaveSection(sectionIndex: number): void {
    if (!this.isSectionReadyForApproval(sectionIndex)) {
      this.markSectionForTeacherModalValidation(sectionIndex);
      return;
    }

    this.submitSectionApproval(sectionIndex, false);
  }

  onConfirmSubmitApproval(): void {
    if (this.pendingApprovalSectionIndex == null || this.isSubmittingApproval) {
      return;
    }

    const sectionIndex = this.pendingApprovalSectionIndex;
    this.submitSectionApproval(sectionIndex, true);
  }

  private submitSectionApproval(sectionIndex: number, showSuccessDialog: boolean): void {
    const payload = this.buildSubmitApprovalPayload(sectionIndex);

    if (payload === null) {
      this.pendingApprovalSectionIndex = null;
      this.submitApprovalConfirmDialog?.close();
      this.openSubmitApprovalErrorDialog('ไม่สามารถส่งขอรับรองได้ กรุณาตรวจสอบข้อมูล Section และผู้สอน');
      return;
    }

    this.isSubmittingApproval = true;
    this.facade.createSubjectScheduleGroup(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
      next: (response) => {
        this.markSectionApprovalSubmitted(sectionIndex, response);
        this.isSubmittingApproval = false;
        this.pendingApprovalSectionIndex = null;
        this.submitApprovalConfirmDialog?.close();
        if (showSuccessDialog) {
          this.submitApprovalSuccessDialog?.open();
        }
      },
      error: (error) => {
        this.isSubmittingApproval = false;
        this.pendingApprovalSectionIndex = null;
        this.submitApprovalConfirmDialog?.close();
        this.openSubmitApprovalErrorDialog(
          this.extractApiErrorMessage(error, 'ไม่สามารถส่งขอรับรองได้ กรุณาลองใหม่อีกครั้ง'),
        );
      },
      });
  }

  onCancelSubmitApproval(): void {
    this.pendingApprovalSectionIndex = null;
  }

  private openSaveSubjectErrorDialog(message: string): void {
    this.saveSubjectErrorMessage = message;
    this.cdr.detectChanges();
    this.saveSubjectErrorDialog?.open();
  }

  private openSubmitApprovalErrorDialog(message: string): void {
    this.submitApprovalErrorMessage = message;
    this.cdr.detectChanges();
    this.submitApprovalErrorDialog?.open();
  }

  private extractApiErrorMessage(error: unknown, fallbackMessage: string): string {
    const responseError = error instanceof HttpErrorResponse
      ? error.error
      : (error as { error?: unknown } | null | undefined)?.error;
    const parsedResponseError = this.parseErrorResponse(responseError);

    if (parsedResponseError != null && typeof parsedResponseError === 'object') {
      const errorRecord = parsedResponseError as { errors?: unknown; error?: unknown; message?: unknown };

      if (Array.isArray(errorRecord.errors) && errorRecord.errors.length > 0) {
        const errors = errorRecord.errors
          .filter((item): item is string => typeof item === 'string' && item.trim() !== '')
          .map((item) => item.trim());

        if (errors.length > 0) {
          return errors.join('\n');
        }
      }

      const errorText = this.getErrorText(errorRecord.error);
      const messageText = this.getErrorText(errorRecord.message);
      const message = errorText !== '' ? errorText : messageText;
      return message !== '' ? message : fallbackMessage;
    }

    const responseErrorText = this.getErrorText(parsedResponseError);
    const messageText = this.getErrorText((error as { message?: unknown } | null | undefined)?.message);
    const message = responseErrorText !== '' ? responseErrorText : messageText;

    return message !== '' ? message : fallbackMessage;
  }

  private parseErrorResponse(value: unknown): unknown {
    if (typeof value !== 'string') {
      return value;
    }

    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  private getErrorText(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  onCancel(): void {
    void this.router.navigate(['/subject-management']);
  }

  isProjectResearchSubjectType(): boolean {
    const selectedSubjectTypeId = this.getSelectedSubjectTypeId();
    const projectResearchSubjectTypeId = this.getProjectResearchSubjectTypeId();

    return selectedSubjectTypeId != null
      && projectResearchSubjectTypeId != null
      && selectedSubjectTypeId === projectResearchSubjectTypeId;
  }

  getTeachingTimes(section: FormGroup): FormArray<FormGroup> {
    return section.get('teachingTimes') as FormArray<FormGroup>;
  }

  getSectionTeachers(sectionIndex: number): SectionTeacherRow[] {
    return this.teacherRowsBySection[sectionIndex] ?? [];
  }

  isSectionSubmitted(sectionIndex: number): boolean {
    return this.submittedSectionStatusBySection[sectionIndex] != null;
  }

  // ส่งกลับแก้ไขพิเศษ hands the section back for editing, so it carries a submitted status
  // without any of the submitted locks.
  isSectionSpecialSentBack(sectionIndex: number): boolean {
    return this.submittedSectionStatusCodeBySection[sectionIndex] === 'special_sent_back';
  }

  getSectionStatusLabel(_sectionIndex: number): string {
    return this.submittedSectionStatusBySection[_sectionIndex] ?? PHASE_ONE_SECTION_STATUS_LABEL;
  }

  getSectionStatus(sectionIndex: number): CreateSectionStatus {
    return this.submittedSectionStatusCodeBySection[sectionIndex] ?? PHASE_ONE_SECTION_STATUS;
  }

  isSectionExpanded(sectionIndex: number): boolean {
    return this.sectionExpandedBySection[sectionIndex] !== false;
  }

  onSectionExpandedChange(sectionIndex: number, expanded: boolean): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    this.sectionExpandedBySection[sectionIndex] = expanded;
  }

  isSectionReadyForApproval(sectionIndex: number): boolean {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return false;
    }

    if (this.isSectionSubmitted(sectionIndex)) {
      return false;
    }

    const section = this.sections.at(sectionIndex);
    const selectedSubject = this.getSelectedSubject();
    const approvalInput = this.getApprovalBuilderInput(sectionIndex);

    if (
      !this.isSaved
      || !selectedSubject
      || this.selectedAcademicTermId == null
      || this.auth.currentUser()?.user.id == null
      || selectedSubject.curriculumTypeId == null
      || approvalInput == null
    ) {
      return false;
    }

    if (!this.isTeacherSectionLocked(sectionIndex) && section.invalid) {
      return false;
    }

    return areApprovalTeachersReady(approvalInput.teachers, approvalInput.studyModeCode);
  }

  getWorkloadHoursText(_sectionIndex: number): string {
    return this.getSelectedSubjectWorkloadHours() || '-';
  }

  getTeachingTimeValues(sectionIndex: number): TeachingTimeValue[] {
    return this.getTeachingTimes(this.sections.at(sectionIndex)).getRawValue() as TeachingTimeValue[];
  }

  private loadTeacherTeachingRangeOptions(sectionIndex: number): Observable<TeachingRangeOption[]> {
    const { academicTerm, year } = this.subjectForm.getRawValue();

    return this.facade.getAcademicTerms(
      academicTerm == null ? null : String(academicTerm),
      toChristianYear(year)
    ).pipe(
      map((terms) => {
        const section = this.sections.at(sectionIndex);
        const academicTerm = terms.at(0);

        return buildTeachingRangeOptions({
          teachingDays: this.getTeachingTimes(section).controls
            .map((time) => toNullableString(time.get('teachingDay')?.value)),
          teachingTimes: this.getTeachingTimeValues(sectionIndex),
          studyStartDate: academicTerm?.study_start_date,
          studyEndDate: academicTerm?.study_end_date,
          midtermStartDate: academicTerm?.midterm_start_date,
          midtermEndDate: academicTerm?.midterm_end_date,
          finalStartDate: academicTerm?.final_start_date,
          finalEndDate: academicTerm?.final_end_date,
          fallbackRanges: this.getSectionTeachers(sectionIndex).map((row) => row.teachingRange),
        });
      }),
      catchError(() => of([]))
    );
  }

  private addSection(options: { isLocked?: boolean; sectionValue?: string } = {}): void {
    const section = this.createSectionForm(options);
    this.sections.push(section);
    const sectionIndex = this.sections.length - 1;
    this.syncSectionWorkloadHours(sectionIndex);
    section.get('section')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateSectionDuplicateErrors());
    section.get('teachingType')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateSectionDuplicateErrors();
        this.syncSectionWorkloadHours(this.sections.controls.indexOf(section));
      });
    this.updateSectionDuplicateErrors();
  }

  private createTeacherDraftForm(row?: SectionTeacherRow): FormGroup {
    return createTeacherDraftForm(this.fb, row);
  }

  private resetTeacherDraftRows(rows: SectionTeacherRow[]): void {
    this.teacherDraftRows.clear();
    rows.forEach((row) => this.teacherDraftRows.push(this.createTeacherDraftForm(row)));
  }

  private isSectionReadyForTeacherModal(sectionIndex: number): boolean {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return false;
    }

    const section = this.sections.at(sectionIndex);

    this.updateSectionDuplicateErrors();
    this.getTeachingTimes(section).updateValueAndValidity({ emitEvent: false });
    section.updateValueAndValidity({ emitEvent: false });

    return section.valid;
  }

  private markSectionForTeacherModalValidation(sectionIndex: number): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    const section = this.sections.at(sectionIndex);
    const teachingTimes = this.getTeachingTimes(section);

    section.markAllAsTouched();
    teachingTimes.markAsDirty();
    teachingTimes.markAsTouched();
    teachingTimes.controls.forEach((time) => time.markAllAsTouched());
    teachingTimes.updateValueAndValidity({ emitEvent: false });
    section.updateValueAndValidity({ emitEvent: false });
  }

  private lockTeacherSection(sectionIndex: number): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    this.teacherSectionLockedBySection[sectionIndex] = true;
    this.disableLockedTeacherSectionControls(this.sections.at(sectionIndex));
  }

  private disableLockedTeacherSectionControls(section: FormGroup): void {
    section.get('workloadHours')?.disable({ emitEvent: false });
    this.getTeachingTimes(section).controls.forEach((time) => time.disable({ emitEvent: false }));
  }


  private buildSubmitApprovalPayload(sectionIndex: number): SubjectScheduleGroupCreatePayload | null {
    const input = this.getApprovalBuilderInput(sectionIndex);
    return input == null ? null : buildSubjectApprovalPayload(input);
  }

  private getApprovalBuilderInput(sectionIndex: number): SubjectApprovalBuilderInput | null {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return null;
    }

    const selectedSubject = this.getSelectedSubject();
    const subjectSection = this.sections.at(sectionIndex);

    return buildApprovalAdapterInput({
      academicTermId: this.selectedAcademicTermId,
      subjectId: selectedSubject?.id ?? null,
      curriculumTypeId: selectedSubject?.curriculumTypeId ?? null,
      courseunit: selectedSubject?.courseunit ?? null,
      createdById: this.auth.currentUser()?.user.id,
      section: {
        section: subjectSection.get('section')?.value,
        teachingType: subjectSection.get('teachingType')?.value,
        room: subjectSection.get('room')?.value,
        studentCount: subjectSection.get('studentCount')?.value,
        teachingTimes: this.getTeachingTimes(subjectSection).controls.map((time) => ({
          teachingDay: time.get('teachingDay')?.value,
          startTime: time.get('startTime')?.value,
          endTime: time.get('endTime')?.value,
        })),
      },
      studyModeId: this.getSelectedSubjectTypeId(),
      studyModeOptions: this.subjectTypeOptions,
      isProjectResearch: this.isProjectResearchSubjectType(),
      workloadMode: this.getTeacherWorkloadModeForSection(sectionIndex),
      teachers: this.getSectionTeachers(sectionIndex),
      scheduleSubmissionUsersByUserId: this.optionsStore.scheduleSubmissionUsersByUserId(),
    });
  }

  getTeacherWorkloadModeForSection(sectionIndex: number): TeacherWorkloadMode {
    return this.teacherWorkloadModeBySection[sectionIndex] ?? 'shared';
  }

  private markSectionApprovalSubmitted(
    sectionIndex: number,
    response: SubjectScheduleGroupCreateResponse
  ): void {
    const responseStatus = response.subject_schedule_group?.status ?? null;
    const status = this.mapApprovalStatus(responseStatus, this.isProjectResearchSubjectType());
    const subjectStatus = this.mapSubjectOverallStatus(responseStatus);

    this.submittedSectionStatusBySection[sectionIndex] = status.label;
    this.submittedSectionStatusCodeBySection[sectionIndex] = status.status;
    this.sectionExpandedBySection[sectionIndex] ??= true;
    this.subjectApprovalStatusLabel = subjectStatus.label;
    this.subjectApprovalStatus = subjectStatus.status;

    if (status.status !== 'special_sent_back') {
      this.teacherSectionLockedBySection[sectionIndex] = true;
      this.sections.at(sectionIndex).disable({ emitEvent: false });
    }

    this.teacherRowsBySection[sectionIndex] = this.getSectionTeachers(sectionIndex).map((row) => ({
      ...row,
      status: status.label,
      statusType: status.status,
    }));
  }

  private mapApprovalStatus(
    status: string | null | undefined,
    isProjectResearch = false
  ): { status: CreateSectionStatus; label: string } {
    const normalizedStatus = String(status ?? '').trim();

    if (isProjectResearch && normalizedStatus === '') {
      return { status: 'pending', label: 'รอดำเนินการ' };
    }

    switch (normalizedStatus) {
      case 'approved':
        return { status: 'approved', label: 'รับรองแล้ว' };
      case 'submitted':
        return isProjectResearch
          ? { status: 'pending', label: 'รอดำเนินการ' }
          : { status: 'pending_approval', label: 'รอพิจารณารับรอง' };
      case 'rejected':
      case 'revision_requested':
        return { status: 'sent_back', label: 'ส่งกลับแก้ไข' };
      case 'special_revision_requested':
        return { status: 'special_sent_back', label: 'ส่งกลับแก้ไขพิเศษ' };
      case 'draft':
        return { status: 'draft', label: 'แบบร่าง' };
      default:
        return { status: 'in_progress', label: getApprovalStatusLabel(status) };
    }
  }

  private mapSubjectOverallStatus(status: string | null | undefined): { status: CreateSectionStatus; label: string } {
    switch (String(status ?? '').trim()) {
      case 'approved':
      case 'completed':
      case 'education_head_confirmed':
      case 'vice_dean_confirmed':
      case 'locked':
        return { status: 'approved', label: 'ดำเนินการสำเร็จ' };
      default:
        return { status: 'in_progress', label: 'อยู่ระหว่างดำเนินการ' };
    }
  }

  private getSelectedTeacherWorkloadMode(): TeacherWorkloadMode {
    return this.teacherModalForm.controls.workloadMode.value === 'notShared' ? 'notShared' : 'shared';
  }

  private openTeacherModal(): void {
    if (this.activeTeacherSectionIndex != null && this.teacherDraftRows.length === 0) {
      this.resetTeacherDraftRows(this.getSectionTeachers(this.activeTeacherSectionIndex));
    }

    this.teacherManagementModal?.open();
  }

  private closeTeacherModal(): void {
    this.teacherManagementModal?.close();
    this.isHydratingTeacherModal = false;
    this.activeTeacherSectionIndex = null;
    this.teacherTeachingRangeOptions = [];
    this.teacherDraftRows.clear();
  }

  private openProjectTeacherModal(sectionIndex: number): void {
    this.activeTeacherSectionIndex = sectionIndex;
    this.editingProjectTeacherIndex = null;
    this.resetProjectTeacherForm();
    this.projectTeacherModal?.open();
  }

  private closeProjectTeacherModal(): void {
    this.projectTeacherModal?.close();
    this.activeTeacherSectionIndex = null;
    this.editingProjectTeacherIndex = null;
    this.resetProjectTeacherForm();
  }

  private resetProjectTeacherForm(): void {
    this.projectTeacherSubmitted = false;
    this.projectTeachingTimes.clear();
    this.projectTeachingTimes.push(this.createProjectTeachingTimeForm());
    this.projectTeacherForm.reset({
      userId: null,
      department: '',
      workloadHours: '0',
      primaryStudentCount: '',
      coStudentCount: '',
      description: '',
      showInSchedule: false,
    }, { emitEvent: false });
    this.projectTeacherForm.get('department')?.disable({ emitEvent: false });
    this.projectTeacherForm.get('workloadHours')?.disable({ emitEvent: false });
    this.projectTeachingTimes.updateValueAndValidity({ emitEvent: false });
  }

  private createProjectTeachingTimeForm(time?: SectionTeacherProjectTeachingTime): FormGroup {
    return createProjectTeachingTimeForm(this.fb, time);
  }

  updateProjectWorkloadHours(): void {
    const value = calculateProjectWorkloadHours(
      this.selectedAcademicTerm?.study_start_date,
      this.selectedAcademicTerm?.study_end_date,
      this.getProjectTeachingTimeValues(),
      {
        midtermStartDate: this.selectedAcademicTerm?.midterm_start_date,
        midtermEndDate: this.selectedAcademicTerm?.midterm_end_date,
        finalStartDate: this.selectedAcademicTerm?.final_start_date,
        finalEndDate: this.selectedAcademicTerm?.final_end_date,
      }
    );

    this.projectTeacherForm.get('workloadHours')?.setValue(value, { emitEvent: false });
  }

  private getProjectTeachingTimeValues(): TeachingTimeValue[] {
    return this.projectTeachingTimes.controls.map((time) => ({
      teachingDay: time.get('teachingDay')?.value,
      startTime: time.get('startTime')?.value,
      endTime: time.get('endTime')?.value,
    }));
  }

  private resolveAcademicTermOptions(): void {
    const { academicTerm, year } = this.subjectForm.getRawValue();
    this.optionsStore.resolveAcademicTerm(
      academicTerm == null ? null : String(academicTerm),
      toChristianYear(year)
    );
  }

  private clearSelectedSubjectDetailForSubjectOptions(_options: SubjectCreateOption[]): void {
    this.clearSelectedSubjectDetail();
  }

  private clearSelectedSubjectDetail(): void {
    this.subjectForm.patchValue({
      subjectCode: null,
      subjectName: '',
      curriculum: '',
      curriculumTypeId: null,
      creditDescription: '',
    }, { emitEvent: false });
    this.applySubjectFieldLocks();
    this.syncAllSectionWorkloadHours();
  }

  private syncSubjectCodeEnabledState(): void {
    const control = this.subjectForm.get('subjectCode');

    if (!control) {
      return;
    }

    const shouldEnable = !this.isSaved && this.getSelectedSubjectTypeId() != null;

    if (shouldEnable && control.disabled) {
      control.enable({ emitEvent: false });
      return;
    }

    if (!shouldEnable && control.enabled) {
      control.disable({ emitEvent: false });
    }
  }

  private getSelectedSubject(): SubjectCreateOption | undefined {
    const subjectId = Number(this.subjectForm.get('subjectCode')?.value);
    return Number.isFinite(subjectId)
      ? this.subjectOptions.find((option) => option.id === subjectId)
      : undefined;
  }

  private getSelectedSubjectTypeId(): number | null {
    const subjectType = this.subjectForm.getRawValue().subjectType as unknown;

    if (typeof subjectType === 'number' && Number.isFinite(subjectType)) {
      return subjectType;
    }

    if (typeof subjectType === 'string' && subjectType.trim() !== '') {
      const studyModeId = Number(subjectType);
      return Number.isFinite(studyModeId) ? studyModeId : null;
    }

    if (typeof subjectType === 'object' && subjectType !== null && 'id' in subjectType) {
      const studyModeId = Number((subjectType as { id?: unknown }).id);
      return Number.isFinite(studyModeId) ? studyModeId : null;
    }

    return null;
  }

  private getProjectResearchSubjectTypeId(): number | null {
    return this.subjectTypeOptions.find((option) => {
      const code = String(option.code ?? '').trim().toUpperCase();
      return code === this.projectResearchSubjectTypeCode;
    })?.id ?? null;
  }

  private createSectionForm(options: { isLocked?: boolean; sectionValue?: string; workloadHours?: string } = {}): FormGroup {
    return createSectionForm(
      this.fb,
      this.isProjectResearchSubjectType(),
      this.digitsOnlyValidators,
      {
        ...options,
        validateWorkloadHours: false,
        workloadHours: options.workloadHours ?? this.getSelectedSubjectWorkloadHours(),
      }
    );
  }

  private getSelectedSubjectWorkloadHours(): string {
    const workloadHours = this.getSelectedSubjectWorkloadHoursValue();

    return workloadHours == null ? '' : this.formatWorkloadHours(workloadHours);
  }

  private getSelectedSubjectWorkloadHoursValue(): number | null {
    const workloadHours = this.getSelectedSubject()?.teachingWorkloadHours;

    return typeof workloadHours === 'number' && Number.isFinite(workloadHours) && workloadHours >= 0
      ? workloadHours
      : null;
  }

  private getSectionDisplayWorkloadHours(
    sectionIndex: number,
    _workloadMode = this.getTeacherWorkloadModeForSection(sectionIndex)
  ): string {
    const subject = this.getSelectedSubject();
    const teachingTypeId = toNumberOrNull(this.sections.at(sectionIndex).get('teachingType')?.value);
    const teachingTypeCode = this.teachingTypeOptions.find((type) => type.id === teachingTypeId)?.code ?? '';
    const workloadHours = deriveTeachingWorkloadHoursFromCourseunit(
      subject?.courseunit,
      teachingTypeCode,
    );

    return this.formatWorkloadHours(workloadHours);
  }

  private syncSectionWorkloadHours(
    sectionIndex: number,
    workloadMode = this.getTeacherWorkloadModeForSection(sectionIndex)
  ): void {
    if (sectionIndex < 0 || sectionIndex >= this.sections.length) {
      return;
    }

    this.sections.at(sectionIndex).get('workloadHours')?.setValue(
      this.getSectionDisplayWorkloadHours(sectionIndex, workloadMode),
      { emitEvent: false }
    );
  }

  private syncAllSectionWorkloadHours(): void {
    this.sectionForms.forEach((_section, sectionIndex) => this.syncSectionWorkloadHours(sectionIndex));
  }

  private formatWorkloadHours(value: number): string {
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
  }

  private reindexTeacherRowsAfterSectionRemoval(removedIndex: number): void {
    const submittedSectionStatusCodeBySection = this.reindexRecord(
      this.submittedSectionStatusCodeBySection,
      removedIndex
    );
    const state = reindexSectionState({
      teacherRowsBySection: this.teacherRowsBySection,
      teacherWorkloadModeBySection: this.teacherWorkloadModeBySection,
      teacherSectionLockedBySection: this.teacherSectionLockedBySection,
      submittedSectionStatusBySection: this.submittedSectionStatusBySection,
      sectionExpandedBySection: this.sectionExpandedBySection,
    }, removedIndex);

    this.teacherRowsBySection = state.teacherRowsBySection;
    this.teacherWorkloadModeBySection = state.teacherWorkloadModeBySection;
    this.teacherSectionLockedBySection = state.teacherSectionLockedBySection ?? {};
    this.submittedSectionStatusBySection = state.submittedSectionStatusBySection;
    this.submittedSectionStatusCodeBySection = submittedSectionStatusCodeBySection;
    this.sectionExpandedBySection = state.sectionExpandedBySection ?? {};
    this.subjectApprovalStatusLabel = Object.values(state.submittedSectionStatusBySection)[0] ?? null;
    this.subjectApprovalStatus = Object.values(submittedSectionStatusCodeBySection)[0] ?? null;
  }

  private reindexRecord<T>(record: Partial<Record<number, T>>, removedIndex: number): Partial<Record<number, T>> {
    return Object.entries(record).reduce<Partial<Record<number, T>>>((result, [key, value]) => {
      const index = Number(key);

      if (!Number.isInteger(index) || value == null || index === removedIndex) {
        return result;
      }

      result[index > removedIndex ? index - 1 : index] = value;
      return result;
    }, {});
  }

  private createTeachingTimeForm(): FormGroup {
    return createTeachingTimeForm(this.fb, this.isProjectResearchSubjectType());
  }

  private applySubject(subjectId: unknown): void {
    const id = Number(subjectId);
    const subject = Number.isFinite(id)
      ? this.subjectOptions.find((option) => option.id === id)
      : undefined;

    this.subjectForm.patchValue({
      subjectName: subject?.name ?? '',
      curriculum: subject?.curriculum ?? '',
      curriculumTypeId: subject?.curriculumTypeId ?? null,
      creditDescription: subject?.courseunit ?? '',
    }, { emitEvent: false });
    this.applySubjectFieldLocks();
    this.syncAllSectionWorkloadHours();
  }

  // หลักสูตร / คำอธิบายหน่วยกิต are entered here and remain editable for both
  // manually-created and synced (REG) subjects. They lock once the subject is saved.
  private applySubjectFieldLocks(): void {
    const lock = this.isSaved;
    const curriculumControl = this.subjectForm.get('curriculumTypeId');
    const creditControl = this.subjectForm.get('creditDescription');

    if (lock) {
      curriculumControl?.disable({ emitEvent: false });
      creditControl?.disable({ emitEvent: false });
      return;
    }

    curriculumControl?.enable({ emitEvent: false });
    creditControl?.enable({ emitEvent: false });
  }

  // Offering-local: capture the entered หลักสูตร / คำอธิบายหน่วยกิต onto the picker
  // option (which the approval payload reads via getSelectedSubject) and refresh the
  // derived workload + section preview. ชื่อรายวิชา stays the master name (read-only).
  private applyLocalSubjectEdits(option: SubjectCreateOption): void {
    const rawValue = this.subjectForm.getRawValue();
    const courseunit = String(rawValue.creditDescription ?? '').trim();

    option.courseunit = courseunit;
    option.curriculumTypeId = toNumberOrNull(rawValue.curriculumTypeId) ?? option.curriculumTypeId;
    option.curriculum = this.curriculumTypeOptions.find((type) => type.id === option.curriculumTypeId)?.name
      ?? option.curriculum;
    option.teachingWorkloadHours = deriveTeachingWorkloadHoursFromCourseunit(courseunit);

    this.syncAllSectionWorkloadHours();
  }

  private updateSectionDuplicateErrors(): void {
    updateSectionDuplicateErrors(this.sectionForms);
  }

  private getSessionAcademicTermName(): string {
    return String(this.auth.currentUser()?.academic_term?.name ?? '1');
  }

  private getDefaultBuddhistYear(): number {
    const sessionYear = Number(this.auth.currentUser()?.academic_term?.year);

    if (Number.isFinite(sessionYear) && sessionYear > 0) {
      return toBuddhistYear(sessionYear);
    }

    return getCurrentBuddhistYear();
  }

  private buildYearOptions(): string[] {
    const baseYear = this.getDefaultBuddhistYear();
    return [baseYear + 1, baseYear, baseYear - 1].map(String);
  }

}
